import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What is an enduring power of attorney?",
    a: "It is a Queensland document appointing someone to make financial and, separately, personal and health decisions for you. Financial power can start immediately or on loss of capacity; personal and health power only ever starts when you lose capacity.",
  },
  {
    q: "Can I make one after a dementia diagnosis?",
    a: "Possibly. A diagnosis does not automatically mean loss of capacity — the question is whether you understand the nature and effect of the document at the time you sign. Timing matters, so do not wait.",
  },
  {
    q: "What is an advance health directive?",
    a: "A document recording your own instructions about future medical treatment, including life-sustaining measures. It speaks directly for you and works alongside an enduring power of attorney.",
  },
  {
    q: "My parent wants to move in and we are extending the house. Do we need an agreement?",
    a: "Yes. Informal granny flat arrangements go wrong more often than people expect — through relationship breakdown, illness, or a sibling dispute after death. A written family agreement records the contribution, the living rights and what happens if it ends, and can affect Centrelink treatment.",
  },
  {
    q: "What if an attorney is misusing their power?",
    a: "Attorneys must act honestly, keep records and keep your money separate from theirs. Where that is not happening, an application can be made to QCAT to review the appointment and to require accounts.",
  },
];

const SERVICES = [
  {
    t: "Enduring powers of attorney",
    d: "Financial, and personal and health. We help you choose attorneys, decide whether they act jointly or severally, appoint substitutes and set the start date — then witness it properly so it holds up.",
  },
  {
    t: "Advance health directives",
    d: "Your own instructions about future treatment and end-of-life care, so your family are not left guessing.",
  },
  {
    t: "Capacity and QCAT",
    d: "Applications for administration and guardianship, reviewing or removing an attorney, and disputes about capacity.",
  },
  {
    t: "Granny flat and family agreements",
    d: "Written agreements where a parent contributes to a home or moves in with family — protecting everyone, including the siblings who are not there.",
  },
  {
    t: "Aged care and retirement villages",
    d: "Reviewing residence contracts and village agreements before you sign, including exit fees and refurbishment terms.",
  },
  {
    t: "Elder financial abuse",
    d: "Where money, property or a transfer has been obtained through pressure. These matters need to be handled carefully and quickly.",
  },
];

export const Route = createFileRoute("/elder-law")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { title: fitTitle(`Elder Law & Powers of Attorney Caloundra | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Enduring powers of attorney, advance health directives, granny flat agreements and aged care contracts. Golden Beach lawyers serving Caloundra."),
      },

      { property: "og:title", content: fitTitle(`Elder Law | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Powers of attorney, advance health directives and family agreements for Sunshine Coast families."),
      },
      { property: "og:url", content: "https://coastsidelaw.com.au/elder-law" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/elder-law" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: ElderLawPage,
});

function ElderLawPage() {
  return (
    <>
      <PageHero
        eyebrow="Elder law"
        title="Planning ahead, while the choice is still yours"
        lead="Powers of attorney, health directives and family agreements. The documents that matter most are the ones that must be made before they are needed — we help you get them in place calmly, and we will come to you if that is easier."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <p className="text-sm leading-relaxed text-foreground">
            An enduring power of attorney cannot be made once capacity is lost. If it is not in place,
            your family may have to apply to QCAT for an administrator or guardian — which takes time,
            costs money, and may not produce the person you would have chosen.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h2 className="font-serif text-xl text-foreground">{s.t}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who you will meet" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm text-muted-foreground">
          Our full guide to{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "enduring-power-of-attorney-qld" }}
            className="font-semibold text-primary underline"
          >
            enduring powers of attorney in Queensland
          </Link>{" "}
          walks through the choices in detail.
        </p>

        <div className="mt-14">
          <EnquiryForm heading="Arrange a planning appointment" />
        </div>
        <div className="mt-14">
          <ExecutorIntakeForm heading="Deceased estate or probate enquiry" />
        </div>
      </section>

      <CtaBand
        title="Home and after-hours appointments available"
        body={`If getting to an office is difficult, say so when you call ${FIRM.phone}. We will come to you.`}
      />
      <div className="h-20" />
    </>
  );
}
